import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { workspaceApi } from "./api";

/**
 * /workspace — SONMI's shared workspace surface, first prototype.
 *
 * Left rail: sessions list. Center: artifacts. Right: SONMI's current
 * understanding (facts vs inferences separated, feedback surface, and
 * shared-reference resolution "what does this refer to?").
 *
 * This is an instrumentation surface, not a finished interface. The
 * visible narration exists so the operator can verify SONMI's
 * perception is accurate before we hide it.
 */

const TYPE_LABELS = {
  code: "Code",
  document: "Document",
  link: "Link",
  transcript: "Transcript",
};

const TYPE_HINT = {
  code: "Paste code. Optionally add a filename in metadata.",
  document: "Paste a document, note, or plain text.",
  link: "URL + a short 'why is this open?' note in metadata.description.",
  transcript: "Terminal output, chat log, or any transcript-shaped text.",
};

/* ---------- top-level page ---------- */
export default function WorkspacePage() {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshSessions = useCallback(async () => {
    try {
      const list = await workspaceApi.listSessions(false);
      setSessions(list);
      setError(null);
      if (list.length && !activeSessionId) setActiveSessionId(list[0].id);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [activeSessionId]);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  const createSession = async () => {
    const s = await workspaceApi.createSession("Untitled session");
    await refreshSessions();
    setActiveSessionId(s.id);
  };

  const archiveSession = async (id) => {
    await workspaceApi.updateSession(id, { archived: true });
    if (id === activeSessionId) setActiveSessionId(null);
    refreshSessions();
  };

  if (error && !sessions.length) {
    return (
      <FullScreenMessage>
        <p>Workspace unreachable.</p>
        <pre style={{ opacity: 0.55, fontSize: 12 }}>{error}</pre>
        <p style={{ opacity: 0.55, fontSize: 13 }}>
          You may need to sign in first.
        </p>
      </FullScreenMessage>
    );
  }

  return (
    <div
      data-testid="workspace-page"
      style={{
        // Fill the ProtectedLayout <main> pane without covering the sidebar.
        // We take the full viewport height and let the parent flex give us
        // the remaining width.
        height: "100vh",
        background: "#0a0a0a",
        color: "rgba(255,255,255,0.9)",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        display: "grid",
        gridTemplateColumns: "240px 1fr 380px",
        overflow: "hidden",
      }}
    >
      <SessionsRail
        sessions={sessions}
        activeId={activeSessionId}
        onSelect={setActiveSessionId}
        onCreate={createSession}
        onArchive={archiveSession}
        loading={loading}
      />
      {activeSessionId ? (
        <SessionSurface
          key={activeSessionId}
          sessionId={activeSessionId}
          onRename={(title) => {
            workspaceApi.updateSession(activeSessionId, { title }).then(refreshSessions);
          }}
        />
      ) : (
        <>
          <FullScreenMessage>
            <p>No session selected.</p>
            <button
              data-testid="workspace-create-session-empty"
              onClick={createSession}
              style={buttonPrimary}
            >
              Open a new session
            </button>
          </FullScreenMessage>
          <div />
        </>
      )}
    </div>
  );
}

/* ---------- sessions rail ---------- */
function SessionsRail({ sessions, activeId, onSelect, onCreate, onArchive, loading }) {
  return (
    <div
      data-testid="workspace-sessions-rail"
      style={{
        borderRight: "1px solid rgba(255,255,255,0.08)",
        padding: "24px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        overflowY: "auto",
      }}
    >
      <div style={{ ...labelStyle }}>Sessions</div>
      <button
        data-testid="workspace-create-session"
        onClick={onCreate}
        style={{ ...buttonGhost, padding: "8px 12px", fontSize: 12 }}
      >
        + New session
      </button>
      {loading && <div style={{ ...labelStyle, opacity: 0.4 }}>loading…</div>}
      {sessions.map((s) => (
        <div
          key={s.id}
          data-testid={`workspace-session-${s.id}`}
          onClick={() => onSelect(s.id)}
          style={{
            padding: "10px 12px",
            borderRadius: 6,
            cursor: "pointer",
            background: s.id === activeId ? "rgba(255,255,255,0.06)" : "transparent",
            border: `1px solid ${s.id === activeId ? "rgba(255,255,255,0.14)" : "transparent"}`,
            transition: "background 120ms",
          }}
        >
          <div style={{ fontSize: 13, lineHeight: 1.35 }}>{s.title}</div>
          <div style={{ fontSize: 10, opacity: 0.42, marginTop: 4, letterSpacing: "0.06em" }}>
            {new Date(s.last_active_at).toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
          {s.id === activeId && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm("Archive this session?")) onArchive(s.id);
              }}
              style={{ ...buttonGhost, padding: "4px 8px", fontSize: 10, marginTop: 6 }}
            >
              Archive
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- session surface (artifacts + understanding) ---------- */
function SessionSurface({ sessionId, onRename }) {
  const [session, setSession] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [understanding, setUnderstanding] = useState(null);
  const [understandingLoading, setUnderstandingLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await workspaceApi.getSession(sessionId);
      setSession(data.session);
      setArtifacts(data.artifacts);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [sessionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Debounced auto-refresh of understanding on structural changes.
  const debounceRef = useRef(null);
  const understandingReqId = useRef(0);
  const refreshUnderstanding = useCallback(async () => {
    const myReq = ++understandingReqId.current;
    setUnderstandingLoading(true);
    try {
      const u = await workspaceApi.getUnderstanding(sessionId);
      // Only apply if this is still the latest in-flight request.
      // Prevents an earlier (pre-upload, empty) response from stomping
      // on a later (post-upload) response.
      if (myReq === understandingReqId.current) {
        setUnderstanding(u);
      }
    } catch (e) {
      if (myReq === understandingReqId.current) {
        setError(e?.response?.data?.detail || e.message);
      }
    } finally {
      if (myReq === understandingReqId.current) {
        setUnderstandingLoading(false);
      }
    }
  }, [sessionId]);

  const scheduleUnderstanding = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(refreshUnderstanding, 400);
  }, [refreshUnderstanding]);

  // Refresh understanding once at mount, then whenever artifacts change count/focus.
  useEffect(() => {
    scheduleUnderstanding();
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifacts.length, session?.current_focus_artifact_id]);

  const focus = async (artifactId) => {
    await workspaceApi.emitEvent(sessionId, {
      type: "artifact_focused",
      artifact_id: artifactId,
    });
    refresh();
  };

  const closeArtifact = async (artifactId) => {
    await workspaceApi.closeArtifact(sessionId, artifactId);
    refresh();
  };

  const addArtifact = async (payload) => {
    await workspaceApi.createArtifact(sessionId, payload);
    setShowAdd(false);
    refresh();
  };

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const uploadFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter(Boolean);
      if (!files.length) return;
      setUploading(true);
      setUploadError(null);
      try {
        await workspaceApi.uploadArtifacts(sessionId, files);
        setShowAdd(false);
        refresh();
      } catch (e) {
        setUploadError(e?.response?.data?.detail || e.message || "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [sessionId, refresh]
  );

  const updateArtifact = async (artifactId, patch) => {
    await workspaceApi.updateArtifact(sessionId, artifactId, patch);
    refresh();
  };

  if (!session) return <FullScreenMessage>{error || "Loading…"}</FullScreenMessage>;
  const focused = artifacts.find((a) => a.id === session.current_focus_artifact_id);

  return (
    <>
      {/* CENTER: artifacts */}
      <ArtifactsColumn
        onDropFiles={uploadFiles}
        uploading={uploading}
      >
        <SessionHeader
          session={session}
          onRename={onRename}
          onAdd={() => setShowAdd(true)}
          onUploadFiles={uploadFiles}
          uploading={uploading}
        />
        {uploadError && (
          <div
            data-testid="workspace-upload-error"
            style={{
              padding: "10px 14px",
              border: "1px solid rgba(255,120,120,0.5)",
              background: "rgba(255,120,120,0.08)",
              color: "rgba(255,200,200,0.9)",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {uploadError}
          </div>
        )}
        {artifacts.length === 0 && (
          <div style={{ ...labelStyle, opacity: 0.5, marginTop: 40 }}>
            No artifacts yet. Open the first one, or drop a file here.
          </div>
        )}
        {artifacts.map((a) => (
          <ArtifactCard
            key={a.id}
            artifact={a}
            isFocused={a.id === focused?.id}
            onFocus={() => focus(a.id)}
            onClose={() => closeArtifact(a.id)}
            onUpdate={(patch) => updateArtifact(a.id, patch)}
          />
        ))}
        {showAdd && (
          <AddArtifactModal
            onCancel={() => setShowAdd(false)}
            onSubmit={addArtifact}
            onUploadFiles={uploadFiles}
            uploading={uploading}
          />
        )}
      </ArtifactsColumn>

      {/* RIGHT: understanding */}
      <UnderstandingPanel
        sessionId={sessionId}
        understanding={understanding}
        loading={understandingLoading}
        onRefresh={refreshUnderstanding}
        artifactsById={Object.fromEntries(artifacts.map((a) => [a.id, a]))}
      />
    </>
  );
}

/* ---------- artifacts column with drag-and-drop ---------- */
function ArtifactsColumn({ children, onDropFiles, uploading }) {
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  const onDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const onDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files && files.length) onDropFiles(files);
  };

  return (
    <div
      data-testid="workspace-artifacts-column"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        position: "relative",
        padding: "24px 32px",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {children}
      {(isDragging || uploading) && (
        <div
          data-testid="workspace-drop-overlay"
          style={{
            position: "absolute",
            inset: 0,
            border: "2px dashed rgba(255,255,255,0.35)",
            background: "rgba(255,255,255,0.04)",
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 50,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontSize: 12,
            color: "rgba(255,255,255,0.85)",
          }}
        >
          {uploading ? "Uploading…" : "Drop file(s) to add as artifact(s)"}
        </div>
      )}
    </div>
  );
}

function SessionHeader({ session, onRename, onAdd, onUploadFiles, uploading }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(session.title);
  const fileInputRef = useRef(null);
  useEffect(() => setTitle(session.title), [session.title]);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        paddingBottom: 16,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <input
            data-testid="workspace-session-title-input"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              setEditing(false);
              if (title.trim() && title !== session.title) onRename(title.trim());
            }}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: 20,
              fontWeight: 300,
              outline: "none",
              width: "100%",
              fontFamily: "inherit",
            }}
          />
        ) : (
          <div
            data-testid="workspace-session-title"
            onClick={() => setEditing(true)}
            style={{ fontSize: 20, fontWeight: 300, cursor: "text" }}
          >
            {session.title}
          </div>
        )}
        <div style={{ ...labelStyle, marginTop: 4 }}>
          started {new Date(session.started_at).toLocaleString()}
        </div>
      </div>
      <input
        ref={fileInputRef}
        data-testid="workspace-upload-file-input"
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files?.length) onUploadFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <button
        data-testid="workspace-upload-btn"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        style={{ ...buttonGhost, opacity: uploading ? 0.5 : 1 }}
      >
        {uploading ? "Uploading…" : "Upload file"}
      </button>
      <button
        data-testid="workspace-add-artifact-btn"
        onClick={onAdd}
        style={buttonPrimary}
      >
        + Artifact
      </button>
    </div>
  );
}

/* ---------- artifact card ---------- */
function ArtifactCard({ artifact, isFocused, onFocus, onClose, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(artifact.content);
  useEffect(() => setContent(artifact.content), [artifact.content]);
  return (
    <div
      data-testid={`workspace-artifact-${artifact.id}`}
      onClick={() => !isFocused && onFocus()}
      style={{
        border: `1px solid ${isFocused ? "rgba(255,255,255,0.32)" : "rgba(255,255,255,0.08)"}`,
        background: isFocused ? "rgba(255,255,255,0.03)" : "transparent",
        borderRadius: 10,
        padding: "16px 18px",
        cursor: isFocused ? "default" : "pointer",
        transition: "border-color 160ms, background 160ms",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              fontSize: 10,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.42)",
              border: "1px solid rgba(255,255,255,0.14)",
              padding: "2px 8px",
              borderRadius: 4,
            }}
          >
            {TYPE_LABELS[artifact.type]}
          </span>
          <span style={{ fontSize: 15, fontWeight: 400 }}>{artifact.title}</span>
          {isFocused && (
            <span
              data-testid={`workspace-artifact-focused-${artifact.id}`}
              style={{
                fontSize: 10,
                color: "rgba(120,220,180,0.8)",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              focused
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditing(!editing);
            }}
            style={{ ...buttonGhost, padding: "4px 10px", fontSize: 11 }}
          >
            {editing ? "Done" : "Edit"}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm("Close this artifact?")) onClose();
            }}
            style={{ ...buttonGhost, padding: "4px 10px", fontSize: 11 }}
          >
            Close
          </button>
        </div>
      </div>
      {editing ? (
        <textarea
          data-testid={`workspace-artifact-content-${artifact.id}`}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onBlur={() => {
            if (content !== artifact.content) onUpdate({ content });
          }}
          style={{
            marginTop: 12,
            width: "100%",
            minHeight: 200,
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 6,
            color: "rgba(255,255,255,0.92)",
            padding: 12,
            fontSize: artifact.type === "code" || artifact.type === "transcript" ? 12 : 13,
            fontFamily:
              artifact.type === "code" || artifact.type === "transcript"
                ? "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace"
                : "inherit",
            resize: "vertical",
            outline: "none",
            lineHeight: 1.5,
            boxSizing: "border-box",
          }}
        />
      ) : (
        <pre
          style={{
            marginTop: 12,
            fontSize: artifact.type === "code" || artifact.type === "transcript" ? 12 : 13,
            fontFamily:
              artifact.type === "code" || artifact.type === "transcript"
                ? "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace"
                : "inherit",
            color: "rgba(255,255,255,0.78)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.5,
            margin: "12px 0 0",
            maxHeight: 260,
            overflow: "auto",
          }}
        >
          {artifact.content || <span style={{ opacity: 0.4 }}>(empty)</span>}
        </pre>
      )}
    </div>
  );
}

/* ---------- add artifact modal ---------- */
function AddArtifactModal({ onCancel, onSubmit, onUploadFiles, uploading }) {
  const [type, setType] = useState("code");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState("");
  const modalFileInputRef = useRef(null);

  const submit = () => {
    if (!title.trim()) return;
    let metadata = {};
    if (meta.trim()) {
      // Parse "key=value, key=value" for convenience. Malformed → empty.
      meta.split(",").forEach((pair) => {
        const [k, ...rest] = pair.split("=");
        if (k && rest.length) metadata[k.trim()] = rest.join("=").trim();
      });
    }
    onSubmit({ type, title: title.trim(), content, metadata });
  };

  return (
    <div
      data-testid="workspace-add-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 20,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 12,
          padding: "28px 32px",
          width: "min(600px, 100%)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div style={{ ...labelStyle, marginBottom: 20 }}>New artifact</div>
        <div
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            border: "1px dashed rgba(255,255,255,0.2)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            fontSize: 12,
            opacity: uploading ? 0.6 : 1,
          }}
        >
          <span style={{ opacity: 0.7 }}>
            Or upload file(s) — .py .js .md .txt .csv .json .vtt .srt .pdf …
          </span>
          <input
            ref={modalFileInputRef}
            data-testid="workspace-add-modal-file-input"
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files?.length) onUploadFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            data-testid="workspace-add-modal-upload-btn"
            onClick={() => modalFileInputRef.current?.click()}
            disabled={uploading}
            style={{ ...buttonGhost, padding: "6px 12px", fontSize: 12 }}
          >
            {uploading ? "Uploading…" : "Choose file(s)"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {["code", "document", "link", "transcript"].map((t) => (
            <button
              key={t}
              data-testid={`workspace-add-type-${t}`}
              onClick={() => setType(t)}
              style={{
                ...buttonGhost,
                padding: "8px 14px",
                fontSize: 12,
                background: type === t ? "rgba(255,255,255,0.14)" : "transparent",
                borderColor: type === t ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.18)",
              }}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </div>
        <div style={{ ...labelStyle, marginBottom: 8, opacity: 0.6 }}>{TYPE_HINT[type]}</div>
        <input
          data-testid="workspace-add-title"
          placeholder={type === "link" ? "Descriptive name (not the URL itself)" : "Title"}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={inputStyle}
        />
        <textarea
          data-testid="workspace-add-content"
          placeholder={
            type === "link"
              ? "URL"
              : type === "code"
              ? "Paste code"
              : type === "transcript"
              ? "Paste transcript"
              : "Paste text"
          }
          value={content}
          onChange={(e) => setContent(e.target.value)}
          style={{ ...inputStyle, minHeight: 180, fontFamily: type === "code" || type === "transcript" ? "monospace" : "inherit" }}
        />
        <input
          data-testid="workspace-add-metadata"
          placeholder="Optional metadata, e.g. filename=payroll.py, language=python"
          value={meta}
          onChange={(e) => setMeta(e.target.value)}
          style={{ ...inputStyle, fontSize: 12 }}
        />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={onCancel} style={buttonGhost}>Cancel</button>
          <button
            data-testid="workspace-add-submit"
            onClick={submit}
            style={buttonPrimary}
            disabled={!title.trim()}
          >
            Open artifact
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- understanding panel ---------- */
function UnderstandingPanel({ sessionId, understanding, loading, onRefresh, artifactsById }) {
  const [askQuery, setAskQuery] = useState("");
  const [askResult, setAskResult] = useState(null);
  const [asking, setAsking] = useState(false);
  const [feedbackNote, setFeedbackNote] = useState(null);

  // Non-blocking observation state: when a refetch is in flight AND we
  // already have a prior understanding on screen, keep the prior content
  // visible but mark it as temporarily stale. Do not clear. Do not spin.
  const isStale = loading && !!understanding;

  const submitFeedback = async (rating) => {
    try {
      await workspaceApi.feedback(sessionId, {
        rating,
        understanding_id: undefined,
      });
      setFeedbackNote(`recorded: ${rating}`);
      setTimeout(() => setFeedbackNote(null), 2400);
    } catch (_) {
      setFeedbackNote("could not record");
    }
  };

  const ask = async () => {
    if (!askQuery.trim()) return;
    setAsking(true);
    setAskResult(null);
    try {
      const r = await workspaceApi.ask(sessionId, askQuery);
      setAskResult(r);
    } finally {
      setAsking(false);
    }
  };

  return (
    <div
      data-testid="workspace-understanding-panel"
      style={{
        borderLeft: "1px solid rgba(255,255,255,0.08)",
        padding: "24px 24px 32px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        overflowY: "auto",
        background: "rgba(255,255,255,0.015)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={labelStyle}>SONMI's current understanding</div>
          {isStale && (
            <span
              data-testid="workspace-understanding-stale"
              style={{
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(255,220,140,0.68)",
                padding: "2px 8px",
                borderRadius: 999,
                border: "1px solid rgba(255,220,140,0.28)",
                background: "rgba(255,220,140,0.06)",
                animation: "sonmiPulse 1600ms ease-in-out infinite",
              }}
              title="A new observation is in flight. The content below is the last confirmed reading."
            >
              observing…
            </span>
          )}
        </div>
        <button
          data-testid="workspace-understanding-refresh"
          onClick={onRefresh}
          disabled={loading}
          style={{ ...buttonGhost, padding: "4px 10px", fontSize: 10 }}
        >
          {loading ? "…" : "refresh"}
        </button>
      </div>

      {/* Ask (shared reference) */}
      <div>
        <div style={{ ...labelStyle, marginBottom: 6, opacity: 0.6 }}>ask about the workspace</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="workspace-ask-input"
            value={askQuery}
            onChange={(e) => setAskQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="What does this refer to?"
            style={{ ...inputStyle, marginBottom: 0, fontSize: 13 }}
          />
          <button
            data-testid="workspace-ask-submit"
            onClick={ask}
            disabled={asking || !askQuery.trim()}
            style={{ ...buttonPrimary, padding: "8px 14px", fontSize: 12 }}
          >
            {asking ? "…" : "Ask"}
          </button>
        </div>
        {askResult && (
          <div data-testid="workspace-ask-result" style={askBoxStyle}>
            {askResult.resolved ? (
              <>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  → {askResult.resolved.title}
                </div>
                <div style={{ ...labelStyle, marginTop: 4 }}>
                  {askResult.resolved.type} · confidence {askResult.confidence}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, opacity: 0.75 }}>
                Could not resolve. {askResult.reasoning}
              </div>
            )}
            <div style={{ ...labelStyle, marginTop: 6, opacity: 0.5, letterSpacing: "0.06em", textTransform: "none" }}>
              {askResult.reasoning}
            </div>
          </div>
        )}
      </div>

      {!understanding && (
        <div style={{ ...labelStyle, opacity: 0.4 }}>{loading ? "listening…" : "no understanding yet"}</div>
      )}

      {understanding && (
        <>
        <div
          data-testid="workspace-understanding-body"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            opacity: isStale ? 0.55 : 1,
            transition: "opacity 260ms ease",
          }}
        >
          {/* Narration */}
          <div data-testid="workspace-narration">
            <div style={{ ...labelStyle, marginBottom: 6, opacity: 0.6 }}>narration</div>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: "rgba(255,255,255,0.82)",
                fontStyle: "italic",
                whiteSpace: "pre-wrap",
              }}
            >
              {understanding.narration || <span style={{ opacity: 0.4 }}>(silent)</span>}
            </div>
          </div>

          {/* Facts */}
          <div data-testid="workspace-facts">
            <div style={{ ...labelStyle, marginBottom: 6 }}>
              facts <span style={{ opacity: 0.4, marginLeft: 6, letterSpacing: "0.04em", textTransform: "none" }}>({understanding.facts.length})</span>
            </div>
            {understanding.facts.length === 0 ? (
              <div style={{ ...labelStyle, opacity: 0.4 }}>none yet</div>
            ) : (
              <ul style={listStyle}>
                {understanding.facts.map((f, i) => (
                  <li key={i} style={obsStyle("fact")}>
                    <span>{f.text}</span>
                    <ArtifactRefs ids={f.ref_artifact_ids} artifactsById={artifactsById} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Inferences */}
          <div data-testid="workspace-inferences">
            <div style={{ ...labelStyle, marginBottom: 6 }}>
              inferences <span style={{ opacity: 0.4, marginLeft: 6, letterSpacing: "0.04em", textTransform: "none" }}>({understanding.inferences.length})</span>
            </div>
            {understanding.inferences.length === 0 ? (
              <div style={{ ...labelStyle, opacity: 0.4 }}>none yet</div>
            ) : (
              <ul style={listStyle}>
                {understanding.inferences.map((f, i) => (
                  <li key={i} style={obsStyle("inference")}>
                    <span>{f.text}</span>
                    <ArtifactRefs ids={f.ref_artifact_ids} artifactsById={artifactsById} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Relationships */}
          {understanding.relationships.length > 0 && (
            <div data-testid="workspace-relationships">
              <div style={{ ...labelStyle, marginBottom: 6 }}>relationships</div>
              <ul style={listStyle}>
                {understanding.relationships.map((r, i) => (
                  <li key={i} style={obsStyle("inference")}>
                    <div style={{ fontSize: 12 }}>
                      <strong>{artifactsById[r.a_artifact_id]?.title || "?"}</strong>
                      {" ↔ "}
                      <strong>{artifactsById[r.b_artifact_id]?.title || "?"}</strong>
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.6, marginTop: 3 }}>
                      {r.kind} — {r.evidence}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

          {/* Feedback (research instrumentation) — stays legible even while stale */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 12, marginTop: "auto" }}>
            <div style={{ ...labelStyle, marginBottom: 8, opacity: 0.6 }}>
              is this understanding accurate?
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["correct", "partly_correct", "incorrect"].map((r) => (
                <button
                  key={r}
                  data-testid={`workspace-feedback-${r}`}
                  onClick={() => submitFeedback(r)}
                  style={{ ...buttonGhost, padding: "6px 12px", fontSize: 11 }}
                >
                  {r.replace("_", " ")}
                </button>
              ))}
            </div>
            {feedbackNote && (
              <div style={{ ...labelStyle, marginTop: 6, opacity: 0.5 }}>{feedbackNote}</div>
            )}
            <div style={{ ...labelStyle, marginTop: 12, opacity: 0.32, letterSpacing: "0.04em", textTransform: "none", fontSize: 10 }}>
              interpreter: {understanding.interpreter} · generated {new Date(understanding.generated_at).toLocaleTimeString()}{isStale ? " · a newer observation is in flight" : ""}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ArtifactRefs({ ids, artifactsById }) {
  if (!ids || !ids.length) return null;
  return (
    <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
      {ids.map((id) => (
        <span key={id} style={{ fontSize: 10, opacity: 0.45, letterSpacing: "0.04em" }}>
          {artifactsById[id]?.title || id.slice(0, 6)}
        </span>
      ))}
    </div>
  );
}

/* ---------- shared styles ---------- */
function FullScreenMessage({ children }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        color: "rgba(255,255,255,0.6)",
        padding: 24,
        gridColumn: "2 / 3",
      }}
    >
      {children}
    </div>
  );
}

const labelStyle = {
  fontSize: 10,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.42)",
};

const buttonPrimary = {
  background: "rgba(255,255,255,0.92)",
  color: "#000",
  border: "none",
  padding: "10px 16px",
  borderRadius: 6,
  fontSize: 12,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  cursor: "pointer",
  fontFamily: "inherit",
};

const buttonGhost = {
  background: "transparent",
  color: "rgba(255,255,255,0.72)",
  border: "1px solid rgba(255,255,255,0.2)",
  padding: "10px 16px",
  borderRadius: 6,
  fontSize: 12,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  cursor: "pointer",
  fontFamily: "inherit",
};

const inputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 6,
  color: "rgba(255,255,255,0.92)",
  padding: "10px 12px",
  fontSize: 14,
  fontFamily: "inherit",
  marginBottom: 10,
  boxSizing: "border-box",
  outline: "none",
};

const askBoxStyle = {
  marginTop: 10,
  padding: "10px 12px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 6,
};

const listStyle = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 };

const obsStyle = (kind) => ({
  fontSize: 13,
  lineHeight: 1.5,
  color: "rgba(255,255,255,0.82)",
  padding: "10px 12px",
  borderRadius: 6,
  background: kind === "fact" ? "rgba(120,220,180,0.05)" : "rgba(200,180,255,0.05)",
  borderLeft: `2px solid ${kind === "fact" ? "rgba(120,220,180,0.5)" : "rgba(200,180,255,0.5)"}`,
});
